import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppStore, type PendingAttachment, type ToolCallEntry } from '../stores/appStore';
import { useTeamChannelStore } from '../stores/teamChannelStore';
import { useSettingsStore } from '../stores/settingsStore';
import { gatewayRef } from '../utils/gatewayRef';
import { registerTeamSession, unregisterTeamSession } from '../utils/teamSessionRegistry';
import { toFriendlyErrorMessage } from '../utils/friendlyErrors';
import { buildApiUrl } from '../utils/serverOrigin';
import ThinkingIndicator from './ThinkingIndicator';
import ToolCallSection from './ToolCallSection';
import type { TeamMessageToolCall } from '../stores/teamChannelStore';
import AttachmentPreviewBar from './AttachmentPreviewBar';
import ContextTokenWarningDialog from './ContextTokenWarningDialog';
import {
  CHAT_UPLOAD_ACCEPT,
  MAX_CHAT_ATTACHMENTS,
  appendUploadedAttachmentsToMessage,
  createPendingAttachment,
  uploadPendingAttachments,
} from '../utils/chatUploads';
import { getSessionTokenCount, shouldShowContextTokenWarning } from '../utils/contextTokenWarning';

interface PopupMessage {
  role: 'user' | 'assistant';
  content: string;
  ts: number;
  thinkingContent?: string;
  toolCalls?: ToolCallEntry[];
}

const AGENT_EMOJIS: Record<string, string> = {
  main: '🔥',
  flint: '🔥',
  yuki: '🌸',
  researcher: '🔬',
  coder: '☕',
};

function parseMaybeJson(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function mapTeamToolCallsToEntries(toolCalls?: TeamMessageToolCall[]): ToolCallEntry[] | undefined {
  if (!toolCalls || toolCalls.length === 0) return undefined;

  return toolCalls.map((tc) => ({
    toolCallId: tc.toolCallId,
    name: tc.name,
    timestamp: Date.now(),
    status: tc.status,
    params: tc.input ? (() => {
      const parsed = parseMaybeJson(tc.input);
      return typeof parsed === 'object' && parsed !== null
        ? parsed as Record<string, unknown>
        : { input: parsed };
    })() : undefined,
    result: tc.output ? parseMaybeJson(tc.output) : undefined,
  }));
}

function buildFallbackAssistantMessage(replyMessage: {
  content: string;
  ts: number;
  thinkingContent?: string;
  toolCalls?: TeamMessageToolCall[];
} | null): PopupMessage | null {
  if (!replyMessage) return null;

  return {
    role: 'assistant',
    content: replyMessage.content,
    ts: replyMessage.ts,
    thinkingContent: replyMessage.thinkingContent,
    toolCalls: mapTeamToolCallsToEntries(replyMessage.toolCalls),
  };
}

function mergeCommittedReplyData(messages: PopupMessage[], fallbackMessage: PopupMessage | null): PopupMessage[] {
  if (!fallbackMessage) return messages;

  const hasFallbackThinking = Boolean(fallbackMessage.thinkingContent);
  const hasFallbackTools = Boolean(fallbackMessage.toolCalls && fallbackMessage.toolCalls.length > 0);
  if (!hasFallbackThinking && !hasFallbackTools) return messages;

  const matchingAssistantIndex = [...messages]
    .map((msg, index) => ({ msg, index }))
    .filter(({ msg }) => msg.role === 'assistant' && msg.content === fallbackMessage.content)
    .at(-1)?.index;

  const lastAssistantIndex = [...messages]
    .map((msg, index) => ({ msg, index }))
    .filter(({ msg }) => msg.role === 'assistant')
    .at(-1)?.index;

  const targetIndex = matchingAssistantIndex ?? lastAssistantIndex;
  if (targetIndex == null) return messages;

  return messages.map((msg, index) => {
    if (index !== targetIndex) return msg;
    return {
      ...msg,
      thinkingContent: msg.thinkingContent ?? fallbackMessage.thinkingContent,
      toolCalls: (msg.toolCalls && msg.toolCalls.length > 0) ? msg.toolCalls : fallbackMessage.toolCalls,
    };
  });
}

export default function AgentReplyPopup() {
  const { popupSessionKey, popupAgentName, popupReplyId, closePopup } = useTeamChannelStore();
  const teamMessages = useTeamChannelStore((state) => state.messages);
  const displayName = useSettingsStore((state) => state.settings.general.displayName);
  const sessions = useAppStore((state) => state.sessions);

  const [messages, setMessages] = useState<PopupMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamText, setStreamText] = useState('');
  const [liveThinkingText, setLiveThinkingText] = useState<string | null>(null);
  const [liveToolCalls, setLiveToolCalls] = useState<ToolCallEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingTokenWarningText, setPendingTokenWarningText] = useState<string | null>(null);
  const [pendingTokenWarningCount, setPendingTokenWarningCount] = useState<number | null>(null);
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const liveThinkingTextRef = useRef<string | null>(null);
  const liveToolCallsRef = useRef<ToolCallEntry[]>([]);

  const agentId = popupSessionKey
    ? (popupSessionKey.split(':')[1] ?? 'main')
    : 'main';
  const agentEmoji = AGENT_EMOJIS[agentId.toLowerCase()] ?? '🤖';
  const agentDisplayName = popupAgentName ?? 'Agent';

  // Resolve sender display name (uppercase)
  const senderDisplayName = (displayName.trim() ||
    (typeof localStorage !== 'undefined' ? localStorage.getItem('clawlet.displayName') : null) ||
    'YOU').toUpperCase();

  // Keep refs in sync so onDone can persist the latest streaming metadata.
  useEffect(() => {
    liveThinkingTextRef.current = liveThinkingText;
  }, [liveThinkingText]);

  useEffect(() => {
    liveToolCallsRef.current = liveToolCalls;
  }, [liveToolCalls]);

  // Load session history on open (Fix 4: fall back to JSONL content if history is empty)
  useEffect(() => {
    if (!popupSessionKey) return;

    setIsLoading(true);
    setMessages([]);

    // Find the original agent reply message from team channel for fallback
    const replyMessage = popupReplyId
      ? teamMessages.find((m) => m.id === popupReplyId)
      : null;

    const fallbackMessage = buildFallbackAssistantMessage(replyMessage);

    const client = gatewayRef.current;
    if (!client) {
      // No gateway — use fallback
      if (fallbackMessage) setMessages([fallbackMessage]);
      setIsLoading(false);
      return;
    }

    // Load history via gateway
    const loadHistory = async () => {
      try {
        const { messages: historyMessages } = await client.getHistory(popupSessionKey, 100);
        const mapped: PopupMessage[] = (historyMessages ?? [])
          .filter((m: any) => m.role === 'user' || m.role === 'assistant')
          .map((m: any) => {
            const rawContent = m.content;
            const textContent = typeof rawContent === 'string'
              ? rawContent
              : Array.isArray(rawContent)
                ? rawContent.filter((b: any) => b.type === 'text').map((b: any) => b.text).join('\n')
                : JSON.stringify(rawContent);

            const thinkingContent = Array.isArray(rawContent)
              ? rawContent
                  .filter((b: any) => b.type === 'thinking' && typeof b.thinking === 'string')
                  .map((b: any) => b.thinking as string)
                  .join('\n\n')
              : undefined;

            const rawToolCalls: ToolCallEntry[] = Array.isArray(m.toolCalls)
              ? m.toolCalls.map((tc: any) => ({
                  toolCallId: tc.toolCallId ?? tc.id ?? '',
                  name: tc.name ?? 'tool',
                  timestamp: Date.now(),
                  status: tc.status ?? 'done',
                  params: tc.params,
                  result: tc.result,
                }))
              : [];

            return {
              role: m.role as 'user' | 'assistant',
              content: textContent,
              ts: typeof m.ts === 'number' ? m.ts : Date.now(),
              thinkingContent: thinkingContent || undefined,
              toolCalls: rawToolCalls.length > 0 ? rawToolCalls : undefined,
            };
          });

        if (mapped.length > 0) {
          // History found — use it, but hydrate the original committed team reply
          // from JSONL if gateway history omitted thinking/tool metadata.
          setMessages(mergeCommittedReplyData(mapped, fallbackMessage));
        } else if (fallbackMessage) {
          // No history — show agent reply from JSONL as initial message
          setMessages([fallbackMessage]);
        }
      } catch (err) {
        console.warn('[AgentReplyPopup] Failed to load history:', err);
        // On error — fall back to the JSONL agent reply
        if (fallbackMessage) setMessages([fallbackMessage]);
      } finally {
        setIsLoading(false);
      }
    };

    void loadHistory();
  }, [popupSessionKey, popupReplyId, teamMessages]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamText]);

  // Focus input on open
  useEffect(() => {
    if (popupSessionKey) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [popupSessionKey]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePopup();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [closePopup]);

  // NOTE: The GatewayClient uses onChatEvent/onAgentEvent callbacks set at
  // construction time — it does NOT expose on()/off() methods. Streaming events
  // for follow-up messages are received via the teamSessionRegistry (registered
  // in handleSend below) which is notified by useGateway.ts / agentEventHandler.ts.

  const handleFilesSelected = useCallback(async (files: FileList | File[]) => {
    const fileList = Array.from(files);
    if (fileList.length === 0) {
      return;
    }

    if (pendingAttachments.length >= MAX_CHAT_ATTACHMENTS) {
      window.alert(`You can send up to ${MAX_CHAT_ATTACHMENTS} files in one message.`);
      return;
    }

    const nextAttachments: PendingAttachment[] = [];
    for (const file of fileList) {
      if (pendingAttachments.length + nextAttachments.length >= MAX_CHAT_ATTACHMENTS) {
        window.alert(`You can send up to ${MAX_CHAT_ATTACHMENTS} files in one message.`);
        break;
      }

      try {
        nextAttachments.push(await createPendingAttachment(file));
      } catch (error) {
        console.error('[AgentReplyPopup] Failed to prepare file for upload:', error);
        window.alert("This file type isn't supported.");
      }
    }

    if (nextAttachments.length > 0) {
      setPendingAttachments((prev) => [...prev, ...nextAttachments]);
    }
  }, [pendingAttachments.length]);

  const clearTokenWarning = useCallback(() => {
    setPendingTokenWarningText(null);
    setPendingTokenWarningCount(null);
  }, []);

  useEffect(() => {
    clearTokenWarning();
  }, [clearTokenWarning, popupSessionKey]);

  const maybeOpenTokenWarning = useCallback((messageText: string, hasAttachments: boolean) => {
    const tokenCount = getSessionTokenCount(sessions, popupSessionKey);
    if (!shouldShowContextTokenWarning({ messageText, hasAttachments, tokenCount })) {
      return false;
    }

    setPendingTokenWarningText(messageText);
    setPendingTokenWarningCount(tokenCount);
    return true;
  }, [popupSessionKey, sessions]);

  const handleSend = useCallback(async (skipTokenWarning = false) => {
    const text = inputValue.trim();
    const queuedAttachments = pendingAttachments;
    if ((!text && queuedAttachments.length === 0) || isStreaming || !popupSessionKey) return;

    if (!skipTokenWarning && maybeOpenTokenWarning(text, queuedAttachments.length > 0)) {
      return;
    }

    const client = gatewayRef.current;
    if (!client) {
      return;
    }

    setIsStreaming(true);
    setStreamText('');
    setLiveThinkingText(null);
    setLiveToolCalls([]);
    liveThinkingTextRef.current = null;
    liveToolCallsRef.current = [];

    try {
      const gatewayToken = useAppStore.getState().gatewayToken;
      const uploadedAttachments = queuedAttachments.length > 0
        ? await uploadPendingAttachments(queuedAttachments, gatewayToken)
        : [];
      const messageText = appendUploadedAttachmentsToMessage(text, uploadedAttachments);
      const userMsg: PopupMessage = { role: 'user', content: messageText, ts: Date.now() };
      setMessages((prev) => [...prev, userMsg]);

      // Register in the team session registry BEFORE sending so that streaming
      // events routed by useGateway.ts / agentEventHandler.ts land here.
      registerTeamSession(popupSessionKey, {
        callId: `popup-${Date.now()}`,
        agentId,
        agentName: agentDisplayName,
        onText: (accumulated) => {
          setStreamText(accumulated);
        },
        onDone: (finalText) => {
          // Snapshot refs BEFORE any state updates. React 18 batches state
          // updaters and may defer execution.
          const thinkingSnapshot = liveThinkingTextRef.current;
          const toolCallsSnapshot = liveToolCallsRef.current.length > 0
            ? [...liveToolCallsRef.current]
            : undefined;

          if (finalText) {
            setMessages((prev) => [
              ...prev,
              {
                role: 'assistant',
                content: finalText,
                ts: Date.now(),
                thinkingContent: thinkingSnapshot ?? undefined,
                toolCalls: toolCallsSnapshot,
              },
            ]);
          }
          setStreamText('');
          setLiveThinkingText(null);
          setLiveToolCalls([]);
          liveThinkingTextRef.current = null;
          liveToolCallsRef.current = [];
          setIsStreaming(false);
          unregisterTeamSession(popupSessionKey);
        },
        onThinking: (content) => {
          liveThinkingTextRef.current = content;
          setLiveThinkingText(content);
        },
        onToolCall: (toolCall) => {
          const parsedInput = toolCall.input ? parseMaybeJson(toolCall.input) : undefined;
          const entry: ToolCallEntry = {
            toolCallId: toolCall.toolCallId,
            name: toolCall.name,
            timestamp: Date.now(),
            status: toolCall.status,
            params: parsedInput && typeof parsedInput === 'object'
              ? parsedInput as Record<string, unknown>
              : parsedInput != null
                ? { input: parsedInput }
                : undefined,
            result: toolCall.output ? parseMaybeJson(toolCall.output) : undefined,
          };
          // Update ref OUTSIDE the state updater so it's immediately available
          // when onDone reads it.
          const prev = liveToolCallsRef.current;
          const idx = prev.findIndex((tc) => tc.toolCallId === toolCall.toolCallId);
          const next = idx >= 0
            ? prev.map((tc, i) => i === idx ? entry : tc)
            : [...prev, entry];
          liveToolCallsRef.current = next;
          setLiveToolCalls(next);
        },
      });

      // Pre-configure session with reasoning + verbose before sending
      await fetch(buildApiUrl('/api/team/enable-reasoning', useAppStore.getState().gatewayUrl), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${gatewayToken}`,
        },
        body: JSON.stringify({ sessionKey: popupSessionKey }),
      }).catch((e: unknown) => console.debug('[AgentReplyPopup] pre-configure err:', e));

      await client.sendMessage(popupSessionKey, messageText);
      setInputValue('');
      setPendingAttachments([]);
    } catch (err) {
      console.error('[AgentReplyPopup] Send error:', err);
      unregisterTeamSession(popupSessionKey);
      setIsStreaming(false);
      window.alert(toFriendlyErrorMessage(err, 'chat-send'));
    }
  }, [agentDisplayName, agentId, inputValue, isStreaming, maybeOpenTokenWarning, pendingAttachments, popupSessionKey, sessions]);

  const handleProceedTokenWarning = () => {
    if (pendingTokenWarningText === null) {
      return;
    }

    clearTokenWarning();
    void handleSend(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  if (!popupSessionKey) return null;

  return (
    <>
    <style>{`
      @keyframes typingBounce {
        0%, 60%, 100% { transform: translateY(0); opacity: 0.6; }
        30% { transform: translateY(-4px); opacity: 1; }
      }
      @keyframes pulse-dot {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.4; }
      }
    `}</style>
    {/* Backdrop */}
    <div
      onClick={closePopup}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
      }}
    >
      {/* Modal */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '80vw',
          maxWidth: '760px',
          height: '85vh',
          maxHeight: '85vh',
          background: 'var(--theme-background)',
          border: '1px solid var(--theme-border)',
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 24px 80px rgba(0, 0, 0, 0.6), 0 0 0 1px var(--theme-accent)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.875rem 1.25rem',
            borderBottom: '1px solid var(--theme-border)',
            flexShrink: 0,
            background: 'var(--theme-card)',
          }}
        >
          {/* Agent avatar */}
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '6px',
              background: 'var(--theme-accent)',
              border: '1px solid var(--theme-primary-20)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1rem',
              flexShrink: 0,
            }}
          >
            {agentEmoji}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontFamily: 'var(--app-code-font)',
                fontSize: '0.6rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--theme-primary)',
              }}
            >
              {agentDisplayName.toUpperCase()}
            </div>
            <div
              style={{
                fontFamily: 'var(--app-code-font)',
                fontSize: '0.55rem',
                color: 'var(--theme-muted-foreground)',
              }}
            >
              team session — continue conversation
            </div>
          </div>

          <button
            type="button"
            onClick={closePopup}
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '6px',
              border: '1px solid var(--theme-border)',
              background: 'transparent',
              color: 'var(--theme-muted-foreground)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.9rem',
              transition: 'all 0.15s ease',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'var(--theme-secondary)';
              (e.currentTarget as HTMLButtonElement).style.color = 'var(--theme-foreground)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
              (e.currentTarget as HTMLButtonElement).style.color = 'var(--theme-muted-foreground)';
            }}
          >
            ✕
          </button>
        </div>

        {/* Message list */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '1.25rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.875rem',
          }}
        >
          {isLoading && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '2rem',
                fontFamily: 'var(--app-code-font)',
                fontSize: '0.6rem',
                color: 'var(--theme-muted-foreground)',
              }}
            >
              Loading history...
            </div>
          )}

          {!isLoading && messages.length === 0 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '2rem',
                fontFamily: 'var(--app-code-font)',
                fontSize: '0.6rem',
                color: 'var(--theme-muted-foreground)',
              }}
            >
              Start the conversation below
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} style={{ display: 'flex', gap: '0.7rem', alignItems: 'flex-start' }}>
              {/* Avatar */}
              {msg.role === 'user' ? (
                <div
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '6px',
                    background: 'var(--theme-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'var(--app-code-font)',
                    fontSize: '0.55rem',
                    fontWeight: 700,
                    color: 'var(--theme-muted-foreground)',
                    flexShrink: 0,
                    marginTop: '2px',
                  }}
                >
                  {senderDisplayName.slice(0, 2)}
                </div>
              ) : (
                <div
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '6px',
                    background: 'var(--theme-accent)',
                    border: '1px solid var(--theme-primary-20)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.9rem',
                    flexShrink: 0,
                    marginTop: '2px',
                  }}
                >
                  {agentEmoji}
                </div>
              )}

              {/* Content column — bubble + committed thinking/tools below */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Bubble */}
                <div
                  style={{
                    borderRadius: '6px',
                    padding: '0.65rem 0.875rem',
                    background: msg.role === 'user'
                      ? 'var(--theme-user-bubble-background)'
                      : 'var(--theme-assistant-bubble-background)',
                  }}
                >
                  <div
                    style={{
                      fontFamily: 'var(--app-code-font)',
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      marginBottom: '0.3rem',
                      color: msg.role === 'user' ? 'var(--theme-muted-foreground)' : 'var(--theme-primary)',
                    }}
                  >
                    {msg.role === 'user' ? senderDisplayName : agentDisplayName.toUpperCase()}
                  </div>
                  <div
                    style={{
                      fontFamily: 'var(--app-code-font)',
                      fontSize: '0.72rem',
                      lineHeight: 1.55,
                      color: 'var(--theme-card-foreground)',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {msg.content}
                  </div>
                </div>

                {/* Committed thinking blocks (below bubble, like ChatPanel history) */}
                {msg.role === 'assistant' && msg.thinkingContent && (
                  <ThinkingIndicator isThinking={false} content={msg.thinkingContent} isCommitted />
                )}

                {/* Committed tool calls (below bubble, like ChatPanel history) */}
                {msg.role === 'assistant' && msg.toolCalls && msg.toolCalls.length > 0 && (
                  <ToolCallSection entries={msg.toolCalls} isCommitted />
                )}
              </div>
            </div>
          ))}

          {/* Streaming message */}
          {isStreaming && (
            <div style={{ display: 'flex', gap: '0.7rem', alignItems: 'flex-start' }}>
              {/* Avatar */}
              <div
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '6px',
                  background: 'var(--theme-accent)',
                  border: '1px solid var(--theme-primary-20)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.9rem',
                  flexShrink: 0,
                  marginTop: '2px',
                }}
              >
                {agentEmoji}
              </div>

              {/* Content column — thinking/tools above bubble, matching ChatPanel live area */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Live thinking indicator (above bubble) */}
                {liveThinkingText && (
                  <div style={{ marginBottom: '0.375rem' }}>
                    <ThinkingIndicator isThinking={!streamText} content={liveThinkingText} />
                  </div>
                )}

                {/* Live tool calls (above bubble) */}
                {liveToolCalls.length > 0 && (
                  <div style={{ marginBottom: '0.375rem' }}>
                    <ToolCallSection entries={liveToolCalls} />
                  </div>
                )}

                {/* Streaming text bubble */}
                <div
                  style={{
                    borderRadius: '6px',
                    padding: '0.65rem 0.875rem',
                    background: 'var(--theme-assistant-bubble-background)',
                  }}
                >
                  <div
                    style={{
                      fontFamily: 'var(--app-code-font)',
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      marginBottom: '0.3rem',
                      color: 'var(--theme-primary)',
                    }}
                  >
                    {agentDisplayName.toUpperCase()}
                  </div>
                  {streamText ? (
                    <div
                      style={{
                        fontFamily: 'var(--app-code-font)',
                        fontSize: '0.72rem',
                        lineHeight: 1.55,
                        color: 'var(--theme-card-foreground)',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {streamText}
                      <span
                        style={{
                          display: 'inline-block',
                          width: '6px',
                          height: '12px',
                          background: 'var(--theme-primary)',
                          marginLeft: '2px',
                          verticalAlign: 'middle',
                          animation: 'pulse-dot 0.8s infinite',
                        }}
                      />
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center', padding: '2px 0' }}>
                      {[0, 0.15, 0.3].map((delay, i) => (
                        <div
                          key={i}
                          style={{
                            width: '5px',
                            height: '5px',
                            borderRadius: '50%',
                            background: 'var(--theme-primary)',
                            animation: `typingBounce 1.3s ease-in-out ${delay}s infinite`,
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input bar */}
        <div
          style={{
            padding: '0.875rem 1.25rem',
            borderTop: '1px solid var(--theme-border)',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            flexShrink: 0,
            background: 'var(--theme-background)',
          }}
        >
          {pendingAttachments.length > 0 ? (
            <AttachmentPreviewBar
              attachments={pendingAttachments}
              onRemove={(id) => setPendingAttachments((prev) => prev.filter((attachment) => attachment.id !== id))}
            />
          ) : null}

          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end' }}>
            <input
              ref={fileInputRef}
              type="file"
              accept={CHAT_UPLOAD_ACCEPT}
              multiple
              className="hidden"
              onChange={(event) => {
                if (event.target.files) {
                  void handleFilesSelected(event.target.files);
                }
                event.target.value = '';
              }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isStreaming || pendingAttachments.length >= MAX_CHAT_ATTACHMENTS}
              title="Attach file"
              style={{
                height: '36px',
                width: '36px',
                borderRadius: '6px',
                border: '1px solid var(--theme-border)',
                background: 'var(--theme-card)',
                color: 'var(--theme-foreground)',
                fontSize: '1rem',
                cursor: 'pointer',
                flexShrink: 0,
                opacity: isStreaming || pendingAttachments.length >= MAX_CHAT_ATTACHMENTS ? 0.4 : 1,
              }}
            >
              📎
            </button>
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={`Continue conversation with ${agentDisplayName}...`}
              disabled={isStreaming}
              rows={1}
              style={{
                flex: 1,
                background: 'var(--theme-input)',
                border: '1px solid var(--theme-border)',
                borderRadius: '6px',
                padding: '0.6rem 0.875rem',
                fontFamily: 'var(--app-code-font)',
                fontSize: '0.72rem',
                color: 'var(--theme-foreground)',
                resize: 'none',
                outline: 'none',
                lineHeight: 1.5,
                minHeight: '36px',
                maxHeight: '120px',
                overflowY: 'auto',
                transition: 'border-color 0.15s ease',
              }}
              onFocus={(e) => { (e.currentTarget as HTMLTextAreaElement).style.borderColor = 'color-mix(in srgb, var(--theme-primary) 30%, transparent)'; }}
              onBlur={(e) => { (e.currentTarget as HTMLTextAreaElement).style.borderColor = 'var(--theme-border)'; }}
            />
            <button
              type="button"
              onClick={() => void handleSend()}
              disabled={(!inputValue.trim() && pendingAttachments.length === 0) || isStreaming}
              style={{
                height: '36px',
                padding: '0 1rem',
                borderRadius: '6px',
                border: 'none',
                background: 'var(--theme-primary)',
                color: 'var(--theme-primary-foreground)',
                fontFamily: 'var(--app-code-font)',
                fontSize: '0.6rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                cursor: 'pointer',
                transition: 'opacity 0.15s ease',
                flexShrink: 0,
                opacity: (!inputValue.trim() && pendingAttachments.length === 0) || isStreaming ? 0.4 : 1,
              }}
            >
              SEND ↵
            </button>
          </div>
        </div>
      </div>

      <ContextTokenWarningDialog
        tokenCount={pendingTokenWarningCount}
        onCancel={clearTokenWarning}
        onProceed={handleProceedTokenWarning}
      />
    </div>
    </>
  );
}
